import { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { authApi } from "../shared/api.ts";
import { checkComposition, checkLength } from "../shared/password-rules.ts";
import { Icon } from "../components/Icon.tsx";
import { AuthLayout } from "../components/AuthLayout.tsx";

/**
 * Operator sign-up. Company + email + password → /api/auth/signup. On success
 * the server emails an activation link; we swap the form for a "check your
 * email" confirmation (mirroring the magic-link sent state). The server is
 * authoritative on validation; the inline strength hint just mirrors the
 * documented rules (8+ chars, letter + number) so the user isn't surprised.
 *
 * No session is established here — activation happens via the emailed link,
 * after which the user signs in. Tenant id is never sent or stored client-side.
 */

export function SignUpPage() {
  const [searchParams] = useSearchParams();
  // Only show the plan hint for known plans — the ?plan param is user-controlled
  // and must not be rendered verbatim as pricing/marketing copy.
  // Scale joined the list when the pricing page started sending buyers here.
  // Still a whitelist rather than a pass-through: the value is user-controlled
  // and is about to be rendered as marketing copy. Enterprise is absent on
  // purpose — it is contract-only and never arrives through self-serve signup.
  const SELF_SERVE_PLANS = ["starter", "growth", "scale"];
  const rawPlan = searchParams.get("plan");
  const planLabel = SELF_SERVE_PLANS.includes(rawPlan ?? "") ? rawPlan : null;
  // The same two params go to the server, which puts them in the verification
  // link so the buyer resumes at the tier they picked. They used to be read
  // ONLY for the label above, which is how a "Start with Growth · annual"
  // click became an ordinary trial with no memory of either choice.
  const rawPeriod = searchParams.get("period");

  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false); // 409 → link to /signin
  const [done, setDone] = useState(false);
  // M4: the "check your inbox" screen had no way to ask for the activation
  // email again -- a typo'd inbox filter or a slow deliverability queue
  // meant the only recovery was leaving this page and starting over from
  // /signin's OWN resend button, which never carried the plan intent below.
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const lengthOk = checkLength(password);
  const compositionOk = checkComposition(password);
  const passwordOk = lengthOk && compositionOk;
  const canSubmit =
    !!company.trim() && !!email.trim() && passwordOk && !submitting;

  const onResend = async () => {
    if (!email.trim()) return;
    setResendError(null);
    setResending(true);
    try {
      await authApi.resendVerification(email.trim(), planLabel, rawPeriod);
      setResent(true);
    } catch {
      // The endpoint is always-200 server-side, so reaching here means the
      // request never landed (offline/DNS/restart). Tell the user to retry.
      setResendError("Couldn't send — check your connection and try again.");
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!company.trim() || !email.trim() || !passwordOk) return;
    setError(null);
    setExists(false);
    setSubmitting(true);
    try {
      const r = await authApi.signup(
        email.trim(), password, company.trim(), planLabel, rawPeriod,
      );
      if (r.ok) {
        setDone(true);
        return;
      }
      // 409 (already exists) surfaces a sign-in link; other errors stay inline.
      if (r.status === 409) {
        setExists(true);
      }
      setError(r.error || "Couldn't create your account. Try again.");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Network error. Check connection and retry.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout className="signin-page">
      <p className="app-eyebrow">
        <span className="dot" /> Satatam · Operator
      </p>
      <h1 className="app-h1">
        {done ? (
          <>
            Check your <em>inbox</em>.
          </>
        ) : (
          <>
            Create your <em>account</em>.
          </>
        )}
      </h1>

      {done ? (
        <p className="app-lede">
          We sent an activation link to{" "}
          <strong style={{ color: "var(--ink)" }}>{email}</strong>. Click it
          to finish setting up, then sign in.
        </p>
      ) : (
        <p className="app-lede">
          One workspace per company. You'll confirm your email before signing
          in.{" "}
          {planLabel ? (
            <span style={{ color: "var(--mint-text)" }}>
              You're on your way to <strong>{planLabel}</strong>.
            </span>
          ) : null}
        </p>
      )}

      {!done && (
        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="signup-company">Company</label>
            <input
              id="signup-company"
              type="text"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={submitting}
              placeholder="Acme Inc."
              required
            />
          </div>

          <div className="field">
            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              placeholder="founder@company.com"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="signup-password">Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                placeholder="At least 8 characters"
                required
                style={{ width: "100%", paddingRight: "64px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--ink-faint)",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "6px 8px",
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {/* Subtle live strength hint — mirrors server rules, not a meter. */}
            <span
              role="status"
              aria-live="polite"
              style={{
                fontFamily: "var(--mono)",
                fontSize: "11.5px",
                letterSpacing: "0.04em",
                color: "var(--ink-faint)",
                display: "flex",
                gap: "14px",
              }}
            >
              <Rule met={lengthOk} label="8+ characters" />
              <Rule met={compositionOk} label="letter + number" />
            </span>
          </div>

          {/* Reserve space so an error never shifts the layout. */}
          <span
            className="err"
            role="alert"
            style={{ minHeight: "16px", display: "block", marginBottom: "12px" }}
          >
            {error || " "}
            {exists ? (
              <>
                {" "}
                <Link to="/signin" style={{ color: "var(--mint-text)" }}>
                  Sign in
                </Link>
              </>
            ) : null}
          </span>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submitting ? "Creating account…" : "Create account →"}
          </button>

          <p
            style={{
              marginTop: "18px",
              fontSize: "13px",
              color: "var(--ink-soft)",
              textAlign: "center",
            }}
          >
            Already have an account?{" "}
            <Link to="/signin" style={{ color: "var(--mint-text)" }}>
              Sign in
            </Link>
          </p>

          <p
            style={{
              marginTop: "24px",
              fontSize: "12.5px",
              color: "var(--ink-faint)",
              fontFamily: "var(--mono)",
              letterSpacing: "0.06em",
            }}
          >
            By creating an account you accept the{" "}
            <a
              href="https://satatam.co/terms.html"
              style={{ color: "var(--mint-text)" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="https://satatam.co/privacy.html"
              style={{ color: "var(--mint-text)" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              privacy policy
            </a>
            .
          </p>
        </form>
      )}

      {done && (
        <div style={{ marginTop: "32px" }}>
          <div style={{ marginBottom: "18px", fontSize: "13px", color: "var(--ink-soft)" }}>
            {resent ? (
              <span style={{ color: "var(--ink-faint)" }}>
                Sent again — check your inbox.
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onResend}
                  disabled={resending}
                >
                  {resending ? "Sending…" : "Resend activation email"}
                </button>
                {resendError ? (
                  <span className="err" role="alert" style={{ marginLeft: "10px" }}>
                    {resendError}
                  </span>
                ) : null}
              </>
            )}
          </div>
          <Link
            to="/signin"
            className="btn btn-ghost"
            style={{ textDecoration: "none" }}
          >
            ← Back to sign in
          </Link>
        </div>
      )}

      <p
        style={{
          marginTop: "48px",
          fontSize: "13px",
          color: "var(--ink-faint)",
          textAlign: "center",
        }}
      >
        <a
          href="https://satatam.co/"
          style={{ color: "var(--ink-soft)" }}
        >
          ← Back to the marketing site
        </a>
      </p>
    </AuthLayout>
  );
}

function Rule({ met, label }: { met: boolean; label: string }) {
  return (
    <span style={{ color: met ? "var(--mint)" : "var(--ink-faint)" }}>
      {met ? <Icon name="check" /> : <Icon name="circle" />} {label}
    </span>
  );
}
